import { useCallback, useContext, useRef } from 'react'
import { useIntl } from 'react-intl'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import TextButton from 'LuxKit/Button/TextButton'
import useToggle from 'hooks/useToggle'
import BodyText from 'LuxKit/Typography/BodyText'
import HeaderContactOptionsModal from 'components/App/Header/HeaderContactOptions/HeaderContactOptionsModal'
import NavigationHeaderContactDropdown from 'components/App/Header/HeaderMain/NavigationHeader/NavigationHeaderContactDropdown'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import GeoContext from 'contexts/geoContext'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import ModalContext from 'contexts/ModalContext'
import ContactContext from 'contexts/contactContext'
import { connect } from 'react-redux'
import { getUpcomingOrders } from 'selectors/orderSelectors'
import { isSupportAssistantEnabled } from 'selectors/featuresSelectors'
import LineCommentsIcon from 'LuxKit/Icons/line/LineCommentsIcon'
import TextLink from 'LuxKit/TextLink'
import styled from 'styled-components'
import { rem } from 'polished'
import { NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'

const homepageRegex = /^\/[a-z]{2}$/

const LesaLinkGroup = styled(Group)`
  padding-top: ${rem(10)};
  padding-bottom: ${rem(10)};
`

interface MappedProps {
  hasUpcomingOrder: boolean
  supportAssistantEnabled: boolean
  path: string
}

function ContactOptions(props: MappedProps) {
  const intl = useIntl()
  const { hasUpcomingOrder, supportAssistantEnabled, path } = props
  const { value: isDropdownOpen, toggle: toggleDropdown, off: closeDropdown } = useToggle(false)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const { currentRegionCode } = useContext(GeoContext)
  const isMobile = useScreenSizeOnly('mobile')
  const showModal = useContext(ModalContext)
  const currentContact = useContext(ContactContext)

  const showLESA = hasUpcomingOrder && supportAssistantEnabled && homepageRegex.test(path)
  const defaultContact = currentContact.defaultContact

  const toggleOpen = useCallback(() => {
    if (isMobile) {
      showModal(<HeaderContactOptionsModal
        supportAssistantFirst={showLESA}
      />)
    } else {
      toggleDropdown()
    }
  }, [isMobile, showLESA, showModal, toggleDropdown])

  const callUs7DaysLabel = intl.formatMessage({
    id: 'jDIGcmj_xdIK6WztH0cF',
    defaultMessage: 'Call us 7 days',
    description: 'Contact tagline for regions without 24/7 support within the travel booking interface',
  })
  const callUs247Label = intl.formatMessage({
    id: 'tiNkVChmVp16MYCeVCYJ',
    defaultMessage: 'Call us 24 hours, 7 days',
    description: 'Contact tagline for regions with 24/7 support within the travel booking interface',
  })
  const contactTagLine = NO_24_7_SUPPORT_REGIONS.includes(currentRegionCode) ? callUs7DaysLabel : callUs247Label

  const contactUsAriaLabel = intl.formatMessage(
    {
      id: '06jG6NgTUJVLaEMXy48b',
      defaultMessage: '{contactTagLine} contact us',
      description: 'Aria label for contact us button combining tagline',
    },
    { contactTagLine },
  )

  return <Group direction="horizontal" verticalAlign="center" gap={4}>
    {!showLESA && <>
      <BodyText variant="small" wrap="no-wrap">{contactTagLine}</BodyText>
      <TextButton
        ref={triggerRef}
        kind="tertiary"
        onClick={toggleOpen}
        aria-expanded={isDropdownOpen}
        aria-label={contactUsAriaLabel}
        size="small"
        startIcon={<LinePhoneIcon />}
        endIcon={<DropdownChevron open={isDropdownOpen} />}
      >
        {defaultContact.humanReadable}
      </TextButton>
      <CSSBreakpoint min="tablet">
        <NavigationHeaderContactDropdown
          anchorRef={triggerRef}
          open={isDropdownOpen}
          onClose={closeDropdown}
          onItemClick={toggleOpen}
        />
      </CSSBreakpoint>
    </>}
    {showLESA && <LesaLinkGroup direction="horizontal" gap={12}>
      <BodyText variant="small">
        {intl.formatMessage({
          id: '56POKhtLVy9UjL5sa-LY',
          defaultMessage: 'Need help with your booking?',
          description: 'Heading for support assistant contact option in the booking flow',
        })}
      </BodyText>
      <TextLink
        to="/support"
        aria-expanded={isDropdownOpen}
        aria-label={contactUsAriaLabel}
        size="small"
        startIcon={<LineCommentsIcon />}
      >
        {intl.formatMessage({
          id: 'Am5VACf7f590P61O5xP0',
          defaultMessage: 'Start a chat 24/7',
          description: 'Hyperlink text to start a chat with support assistant',
        })}
      </TextLink>
    </LesaLinkGroup>}
  </Group>
}

export default connect((state: App.State) => ({
  hasUpcomingOrder: !!getUpcomingOrders(state).length,
  supportAssistantEnabled: isSupportAssistantEnabled(state),
  path: state.router.location.pathname,
}))(ContactOptions)
